import { useEffect } from "react";
import {useLocation, useNavigate} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import {useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage, useTheme} from "@/comn/hooks";
import { Link } from "react-router-dom";
import {
    SF_REVW_DTL_FORM,
    SF_REVW_SRCH,
    SG_REVW_LIST,
    URLS,
    APIS
} from "@/tra/tancis/ptli/revw/cstmr/services/PtliRevwService";

/**
 * PTLI0707013Q Component
 * -----------------------
 * - Displays customer review details (read-only).
 * - Fetches review data based on the `revwPrcssId` from the URL.
 * - Converts numeric rating to star symbols (★ for filled, ☆ for empty).
 * - Populates form fields with fetched data; all fields are non-editable.
 * - Back button navigates to the review list page.
 */

export const PTLI0707013Q = (props: any) => {

    const { condData, callback, cstmRoleList} = props;
    const { pgeUid } = usePage({
        pgeUid: "ptli0707013q", 
        auth: ["auth_ptli0019"]
    });
    const modal    = useModal();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const {t} = useTranslation();

    // Extract review process ID from the URL path
    const location = useLocation();
    const pathParts = location.pathname.split("/");
    const revwPrcssId = pathParts[pathParts.length - 1];


    // Resource initialization (common codes for dropdowns, etc.)
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "COM0024" },
        ],
    });

    // Form to hold review information (read-only fields)
    const form = {
        revwInfo: useForm({
            defaultSchema: SF_REVW_DTL_FORM,
            defaultValues: { ...pgeStore?.form } || {},
        }),
    };

    // Fetch review detail from API
    const fetch = {
        getCstmrRevw: useFetch({
            api: (id) => APIS.getRevwPrcss(id),
            onSuccess: (data) => {
                const content = data?.cstmrRevw?.content;
                if (content) {

                    const ratingNum = content.rating ?? 0;
                    const fullStars = "★".repeat(ratingNum);
                    const emptyStars = "☆".repeat(5 - ratingNum);
                    const stars = fullStars + emptyStars;

                    form.revwInfo.setValues({
                        usrId: content.usrId,
                        revwPrcssNm: content.usrFdbckPrcssNm,
                        bsopClsfCd: content.bsopClsfCd,
                        revwCmnt: content.revwCmnt,
                        revwTags: content.tags,
                        revwStr: stars,
                        trgtReff: content.trgtReff
                    });
                }
            },
            onError: (error: any) =>
                modal.openModal({ content: error.response.data.errorMessage }),
        }),
    };

    // Fetch review details on component mount or when ID changes
    useEffect(() => {
        fetch.getCstmrRevw.fetch(revwPrcssId)
    }, [revwPrcssId]);

    // Render review detail form (all fields disabled)
    // + Back button to return to list view
    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.revwPrcssNm} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.bsopClsfCd} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.usrId} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.trgtReff} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.revwStr} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.revwTags} edit={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.revwInfo.schema.revwCmnt} edit={false} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button role="list" onClick={() => navigate(URLS.ptli0707012q)} />
                </Layout.Left>
            </Layout>

        </Page>
    );
};
